import { Typography } from '@euler/app/components/typography';
import { EmptyPlaceholder } from '@euler/app/tv/components/EmptyPlaceholder';
import { ConstructionJobDetailView } from '@euler/app/tv/components/construction/JobRowDetailView';
import {
  UnscheduledJobsView,
  useUnscheduledJobs,
} from '@euler/app/tv/components/construction/UnscheduledJobsView';
import { useScreenContentPadding } from '@euler/app/tv/functions/utils';
import { usePresentationTheme } from '@euler/app/tv/theme';
import { useContainerLayout } from '@euler/components/layout/LayoutProvider';
import { HStack } from '@euler/components/layout/Stack';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { InventoryQuery } from '@euler/functions/useInventory';
import { ConstructionJob, TaskDetail } from '@euler/model/task-detail';
import { memo, useCallback, useMemo } from 'react';
import {
  FlatList,
  ListRenderItem,
  StyleProp,
  View,
  ViewStyle,
} from 'react-native';

type RowModel = { key: string } & (
  | {
      type: 'unscheduled-jobs';
      unscheduledWorkItems: ReturnType<typeof useUnscheduledJobs>;
    }
  | {
      type: 'section-title';
      title: string;
      count: number;
    }
  | {
      type: 'construction-job';
      job: ConstructionJob;
    }
  | {
      type: 'empty-placeholder';
    }
);

export const ConstructionOverview = memo(
  ({
    style,
    detail,
    inventoryQuery,
    onConstructionJobPress,
  }: {
    style?: StyleProp<ViewStyle>;
    detail: TaskDetail;
    inventoryQuery: InventoryQuery;
    onConstructionJobPress?: (job: ConstructionJob) => void;
  }) => {
    const theme = usePresentationTheme();
    const { rpx } = useResponsiveContext();
    const [marginLeft, marginRight] = useScreenContentPadding();
    const { height } = useContainerLayout();
    const unscheduledWorkItems = useUnscheduledJobs(detail, inventoryQuery);

    const rowModels = useMemo(() => {
      const models: RowModel[] = [];
      if (unscheduledWorkItems.length > 0) {
        models.push({
          key: 'unscheduled-jobs-title',
          type: 'section-title',
          title: '待报价项目',
          count: unscheduledWorkItems.length,
        });
        models.push({
          key: 'unscheduled-jobs',
          type: 'unscheduled-jobs',
          unscheduledWorkItems,
        });
      }
      if (detail.construction.jobs.length > 0) {
        models.push({
          key: 'jobs-section-title',
          type: 'section-title',
          title: '已派工项目',
          count: detail.construction.jobs.length,
        });
        for (const job of detail.construction.jobs) {
          models.push({ key: `job-${job.id}`, type: 'construction-job', job });
        }
      } else {
        models.push({ key: 'empty-placeholder', type: 'empty-placeholder' });
      }
      return models;
    }, [detail.construction.jobs, unscheduledWorkItems]);

    const renderItem: ListRenderItem<RowModel> = useCallback(
      ({ item }) => {
        if (item.type === 'section-title') {
          return (
            <HStack
              justifyContent="flex-start"
              css={`
                margin-bottom: ${rpx(32)}px;
              `}
            >
              <Typography.Label
                medium
                size={rpx(32)}
                color={theme.task.construction.sectionTitleTextColor}
                lineHeight={rpx(48)}
                css={`
                  margin-left: ${marginLeft}px;
                `}
              >
                {item.title}
              </Typography.Label>
              <Typography.Label
                light
                size={rpx(32)}
                lineHeight={rpx(48)}
                color={theme.task.construction.sectionCounterTextColor}
              >
                {` ${item.count}`}
              </Typography.Label>
            </HStack>
          );
        }

        if (item.type === 'empty-placeholder') {
          return (
            <EmptyPlaceholder
              text="暂无相关施工项目"
              iconSize={rpx(100)}
              textStyle={{ marginTop: rpx(32) }}
              css={`
                //flex: 1;
                height: ${height}px;
              `}
            />
          );
        }

        if (item.type === 'unscheduled-jobs') {
          return (
            <UnscheduledJobsView
              workItems={item.unscheduledWorkItems}
              css={`
                margin-bottom: ${rpx(40)}px;
              `}
            />
          );
        }

        return (
          <ConstructionJobDetailView
            job={item.job}
            onPress={onConstructionJobPress}
            css={`
              margin-bottom: ${rpx(32)}px;
              margin-left: ${marginLeft}px;
              margin-right: ${marginRight}px;
            `}
          />
        );
      },
      [
        height,
        marginLeft,
        marginRight,
        onConstructionJobPress,
        rpx,
        theme.task.construction.sectionCounterTextColor,
        theme.task.construction.sectionTitleTextColor,
      ],
    );

    return (
      <View
        css={`
          margin-bottom: ${-rpx(148)}px;
        `}
        style={style}
      >
        <FlatList
          data={rowModels}
          keyExtractor={x => x.key}
          renderItem={renderItem}
          contentContainerStyle={{
            flexGrow: 1,
            paddingBottom: rpx(148),
          }}
        />
      </View>
    );
  },
);
